import React from "react";
import { useNavigate } from "react-router-dom";
import { colors, fonts } from "../theme";
import { View, Text, Card, Press, ScreenHeader, SectionTitle } from "../ui";
import { Icon } from "../Icon";
import { useI18n } from "../i18n";
import { useAppState } from "../state";
import { AREAS, EXAMS, t } from "../content";

function Row({ icon, color, title, sub, onPress, testID }) {
  return (
    <Press testID={testID} onClick={onPress}>
      <Card style={{ flexDirection: "row", alignItems: "center", gap: 14, marginBottom: 12 }}>
        <View style={{ width: 46, height: 46, borderRadius: 13, backgroundColor: colors.surfaceSecondary, alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
          <Icon name={icon} size={22} color={color} weight="fill" />
        </View>
        <View style={{ flex: 1, minWidth: 0 }}>
          <Text style={{ fontWeight: fonts.bold, fontSize: 15, color: colors.onSurface }}>{title}</Text>
          <Text style={{ fontWeight: fonts.regular, fontSize: 12, color: colors.muted }}>{sub}</Text>
        </View>
        <Icon name="caret-right" size={18} color={colors.muted} />
      </Card>
    </Press>
  );
}

export default function Exams() {
  const navigate = useNavigate();
  const { tr, lang } = useI18n();
  const { state } = useAppState();
  const start = (id) => navigate(`/session?mode=exam&examId=${id}`);
  const mocks = EXAMS.filter((e) => e.type === "mock");
  const mixed = EXAMS.find((e) => e.id === "mixed");

  return (
    <View style={{ flex: 1, backgroundColor: colors.surfaceSecondary }}>
      <ScreenHeader title={tr("exams")} big />
      <View style={{ padding: 20 }}>
        <Row testID="exam-quick" icon="lightning" color={colors.brandPrimary} title={tr("quickTest")} sub={`10 ${tr("questions")}`} onPress={() => start("quick")} />
        <Row testID="exam-mixed" icon="shuffle" color={colors.info} title={tr("mixedExam")} sub={`${mixed.count} ${tr("questions")}`} onPress={() => start("mixed")} />

        <SectionTitle style={{ marginTop: 8 }}>{tr("mockExams")}</SectionTitle>
        {mocks.map((m) => (
          <Row key={m.id} testID={`exam-${m.id}`} icon="cap" color={colors.success} title={t(m.title, lang)} sub={`${m.count} ${tr("questions")}`} onPress={() => start(m.id)} />
        ))}

        <SectionTitle style={{ marginTop: 8 }}>{tr("moduleExams")}</SectionTitle>
        {AREAS.map((a) => (
          <Row key={a.id} testID={`exam-module-${a.id}`} icon={a.icon} color={colors.brandPrimary} title={`${a.num} · ${t(a.title, lang)}`} sub={`12 ${tr("questions")}`} onPress={() => start(`module_${a.id}`)} />
        ))}

        <SectionTitle style={{ marginTop: 8 }}>{tr("examHistory")}</SectionTitle>
        {state.exams.length === 0 ? (
          <Card>
            <Text style={{ fontWeight: fonts.regular, fontSize: 14, color: colors.muted, textAlign: "center", padding: "8px 0", width: "100%" }}>{tr("noExamsYet")}</Text>
          </Card>
        ) : (
          state.exams.slice(0, 15).map((e, i) => {
            const good = e.score >= 60;
            const reviewable = !!(e.answers && e.answers.length);
            return (
              <Press key={i} testID={`exam-history-${i}`} disabled={!reviewable} onClick={() => reviewable && navigate(`/exam-review/${i}`)}>
                <Card style={{ flexDirection: "row", alignItems: "center", gap: 12, marginBottom: 10 }}>
                  <View style={{ width: 46, height: 46, borderRadius: 23, backgroundColor: good ? colors.successSurface : colors.errorSurface, alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                    <Text style={{ fontWeight: fonts.bold, fontSize: 14, color: good ? colors.success : colors.error }}>{e.score}%</Text>
                  </View>
                  <View style={{ flex: 1, minWidth: 0 }}>
                    <Text numberOfLines={1} style={{ fontWeight: fonts.semibold, fontSize: 14, color: colors.onSurface }}>{e.title}</Text>
                    <Text style={{ fontWeight: fonts.regular, fontSize: 12, color: colors.muted }}>{e.correct}/{e.total} · {new Date(e.date).toLocaleDateString(lang === "de" ? "de-CH" : "en-GB")}</Text>
                  </View>
                  {reviewable ? <Icon name="caret-right" size={18} color={colors.muted} />
                    : <Text style={{ fontWeight: fonts.medium, fontSize: 12, color: good ? colors.success : colors.error }}>{good ? tr("passed") : tr("failed")}</Text>}
                </Card>
              </Press>
            );
          })
        )}
      </View>
    </View>
  );
}
